import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling'
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling'
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, OnDestroy, output, ViewChild } from '@angular/core'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { OrganizationUser, USER_ROLES } from '@app/v2/shared/types/user.type'
import { isElementVisible, isNearScrollBottom, isViewportOverflowing } from '@app/v2/shared/utils/viewport-scroll.util'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { USER_STATUS_ACTION } from '../constants/status-dropdown-options'
import { orderTeamUsersForDisplay } from '../helpers/order-team-users.helper'
import { UserCardComponent } from './user-card/user-card.component'

@Component({
  selector: 'app-users-list',
  imports: [UserCardComponent, NgxSkeletonLoaderModule, ProgressCircleComponent, ScrollingModule, ExperimentalScrollingModule],
  standalone: true,
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsersListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) private _viewport?: CdkVirtualScrollViewport
  @ViewChild('viewportHost') private _viewportHost?: ElementRef<HTMLElement>

  public users = input<OrganizationUser[]>([])
  public isLoading = input<boolean>(false)
  public isLoadingMore = input<boolean>(false)
  public hasMore = input<boolean>(false)
  public isStatusActionsDisabled = input<boolean>(false)

  public loadMore = output<void>()
  public statusActionSelected = output<{ user: OrganizationUser; action: USER_STATUS_ACTION }>()
  public updateTeamMemberRole = output<{ user: OrganizationUser; role: USER_ROLES }>()

  private readonly _workspaceRoleService = inject(WorkspaceRoleService)

  /** Owner first, then current user (when not owner), then the rest. */
  public displayUsers = computed(() => orderTeamUsersForDisplay(this.users(), this._workspaceRoleService.getCurrentUserId()))

  private _scrollAnimationFrame: number | null = null
  private _viewportSyncFrame: number | null = null
  private _viewportPostSyncFrame: number | null = null
  private _resizeObserver?: ResizeObserver
  private _isDestroyed = false

  constructor() {
    effect(() => {
      this.displayUsers()
      this.isLoading()
      if (!this._isDestroyed) this._scheduleViewportSync()
    })
  }

  ngAfterViewInit(): void {
    this._initResizeObserver()
  }

  ngOnDestroy(): void {
    this._isDestroyed = true
    this._resizeObserver?.disconnect()

    if (this._scrollAnimationFrame !== null) {
      cancelAnimationFrame(this._scrollAnimationFrame)
    }

    if (this._viewportSyncFrame !== null) cancelAnimationFrame(this._viewportSyncFrame)

    if (this._viewportPostSyncFrame !== null) cancelAnimationFrame(this._viewportPostSyncFrame)
  }

  /**
   * @description Forward status action selection from a user card
   */
  public onStatusActionSelected(event: { user: OrganizationUser; action: USER_STATUS_ACTION }): void {
    this.statusActionSelected.emit(event)
  }

  /**
   * @description Forward team member role update from a user card
   * @param event { user: OrganizationUser; role: USER_ROLES }
   * @returns {void}
   */
  public onUpdateTeamMemberRole(event: { user: OrganizationUser; role: USER_ROLES }): void {
    this.updateTeamMemberRole.emit(event)
  }

  /**
   * @description Track users in the virtual scroll viewport
   * @param _index number
   * @param user OrganizationUser
   * @returns number
   */
  public trackByUserId = (_index: number, user: OrganizationUser): number => user.id

  /**
   * @description Load the next page when the virtual scroll viewport nears the bottom
   */
  public onViewportScroll(): void {
    const viewport = this._viewport?.elementRef.nativeElement
    if (!viewport || !this._isViewportVisible()) return

    if (this._scrollAnimationFrame !== null) {
      cancelAnimationFrame(this._scrollAnimationFrame)
    }

    this._scrollAnimationFrame = requestAnimationFrame(() => {
      this._scrollAnimationFrame = null

      if (this._isDestroyed || !this._isViewportVisible() || this.isLoading() || this.isLoadingMore() || !this.hasMore()) return

      if (isNearScrollBottom(viewport)) this.loadMore.emit()
    })
  }

  /**
   * @description Whether the virtual scroll viewport is visible in the layout
   */
  private _isViewportVisible(): boolean {
    return isElementVisible(this._viewport?.elementRef.nativeElement)
  }

  /**
   * @description Observe viewport host resizes so autosize can re-measure rows
   */
  private _initResizeObserver(): void {
    const host = this._viewportHost?.nativeElement
    if (!host || this._resizeObserver || typeof ResizeObserver === 'undefined') return

    this._resizeObserver = new ResizeObserver(() => {
      if (!this._isDestroyed) this._scheduleViewportSync()
    })
    this._resizeObserver.observe(host)
  }

  /**
   * @description Re-measure the virtual scroll viewport after data or layout changes
   */
  private _scheduleViewportSync(): void {
    if (this._isDestroyed || this._viewportSyncFrame !== null) return

    if (this._viewportPostSyncFrame !== null) {
      cancelAnimationFrame(this._viewportPostSyncFrame)
      this._viewportPostSyncFrame = null
    }

    this._viewportSyncFrame = requestAnimationFrame(() => {
      this._viewportSyncFrame = null

      if (this._isDestroyed || !this._isViewportVisible()) return

      this._viewport?.checkViewportSize()

      this._viewportPostSyncFrame = requestAnimationFrame(() => {
        this._viewportPostSyncFrame = null

        if (this._isDestroyed || !this._isViewportVisible()) return

        this._viewport?.checkViewportSize()
        this._syncViewportHeight()
        this._loadMoreIfViewportNotFilled()
      })
    })
  }

  /**
   * Fit viewport height to rendered rows up to the max-height cap.
   * Hides overflow when content fits so zoom/subpixels cannot create a one-row scrollbar.
   */
  private _syncViewportHeight(): void {
    const viewport = this._viewport?.elementRef.nativeElement
    const wrapper = viewport?.querySelector('.cdk-virtual-scroll-content-wrapper') as HTMLElement | null
    if (!viewport || !wrapper) return

    const maxHeight = this._getViewportMaxHeight()
    // Ceil rect height to avoid subpixel underflow (common at non-100% browser zoom).
    const contentHeight = Math.ceil(Math.max(wrapper.scrollHeight, wrapper.getBoundingClientRect().height))
    if (contentHeight <= 0) {
      // First pass: keep the cap height so autosize can measure row(s).
      const bootstrapPx = `${maxHeight}px`
      if (viewport.style.height !== bootstrapPx) {
        viewport.style.height = bootstrapPx
        this._viewport?.checkViewportSize()
      }
      return
    }

    const needsScroll = contentHeight > maxHeight
    const nextHeight = needsScroll ? maxHeight : contentHeight
    const nextHeightPx = `${nextHeight}px`

    viewport.classList.toggle('users-list-viewport--scrollable', needsScroll)
    viewport.style.overflowY = needsScroll ? 'auto' : 'hidden'

    if (viewport.style.height !== nextHeightPx) {
      viewport.style.height = nextHeightPx
      this._viewport?.checkViewportSize()
    }
  }

  /**
   * Max viewport height in CSS pixels: min(28rem, 50vh), matching previous Tailwind caps.
   */
  private _getViewportMaxHeight(): number {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return Math.floor(Math.min(28 * rootFontSize, window.innerHeight * 0.5))
  }

  /**
   * @description Load more when the first page does not fill the viewport
   */
  private _loadMoreIfViewportNotFilled(): void {
    if (this._isDestroyed || !this._isViewportVisible() || this.isLoading() || this.isLoadingMore() || !this.hasMore()) return

    const element = this._viewport?.elementRef.nativeElement
    if (!element) return

    if (!isViewportOverflowing(element)) this.loadMore.emit()
  }
}
